import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import useComponentVisible from '../ComponentVisible';
import { useJoyrideContext } from '../../../contexts/JoyrideContext';

const getItemKey = (item) => {
    if (item === null || item === undefined) return null;
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (item.val !== undefined && item.val !== null) return String(item.val);
    if (item.id !== undefined && item.id !== null) return String(item.id);
    if (item._id !== undefined && item._id !== null) return String(item._id);
    if (item.value !== undefined && item.value !== null) return String(item.value);
    if (item.name !== undefined && item.name !== null) return String(item.name);
    return null;
};

const getItemLabel = (item) => {
    if (item === null || item === undefined) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    return item.name ?? item.label ?? item.title ?? item.role_name ?? (item.value !== undefined ? String(item.value) : (item.val !== undefined ? String(item.val) : ''));
};

const Dropdown = ({
    items = [],
    callback,
    width = "100",
    isWidthInPercentage = true,
    disabled = false,
    searchable = false,
    multiSelect = false,
    noItemSelectedTitle = multiSelect ? "Select Items" : "Select an option",
    initialValue = multiSelect ? [] : null,
    id = "",
    error,
    className = "",
    selectClassName = "",
    menuClassName = ""
}) => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const joyrideContext = useJoyrideContext();
    const joyRideState = joyrideContext?.state || {};
    const [selectedItems, setSelectedItems] = useState(multiSelect ? [] : null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredItems, setFilteredItems] = useState(items || []);
    const [dropUp, setDropUp] = useState(false);

    useEffect(() => {
        if (isComponentVisible && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setDropUp(spaceBelow < 240 && spaceAbove > spaceBelow);
        }
    }, [isComponentVisible]);

    useEffect(() => {
        // clear selection if items change and selected items are no longer valid
        if (multiSelect) {
            const validSelectedItems = selectedItems?.filter(selected => {
                const sKey = getItemKey(selected);
                return items?.some(item => getItemKey(item) === sKey);
            });
            if (validSelectedItems?.length !== selectedItems?.length) {
                setSelectedItems(validSelectedItems);
            }
        } else {
            if (selectedItems) {
                const sKey = getItemKey(selectedItems);
                if (!items?.some(item => getItemKey(item) === sKey)) {
                    setSelectedItems(null);
                }
            }
        }
    }, [items]);

    useEffect(() => {
        if (multiSelect) {
            if (Array.isArray(initialValue)) {
                setSelectedItems(initialValue);
            }
        } else {
            setSelectedItems(initialValue || null);
        }
    }, [initialValue, multiSelect]);

    useEffect(() => {
        if (!items || !Array.isArray(items)) {
            setFilteredItems([]);
            return;
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const filtered = items.filter(item => {
                const label = getItemLabel(item);
                const desc = item?.description ? String(item.description).toLowerCase() : '';
                return label.toLowerCase().includes(query) || desc.includes(query);
            });
            setFilteredItems(filtered);
        } else {
            setFilteredItems(items);
        }
    }, [searchQuery, items]);

    const handleOpenMenu = () => {
        if (!disabled) {
            setIsComponentVisible(!isComponentVisible);
        }
    };

    const handleSelect = (item) => {
        if (item?.disabled) return;

        if (multiSelect) {
            const itemKey = getItemKey(item);
            let updatedSelection;
            if (selectedItems.some((selected) => getItemKey(selected) === itemKey)) {
                updatedSelection = selectedItems.filter((selected) => getItemKey(selected) !== itemKey);
            } else {
                updatedSelection = [...selectedItems, item];
            }
            setSelectedItems(updatedSelection);
            if (typeof callback === 'function') {
                callback(updatedSelection.map((selected) => getItemKey(selected)));
            }
        } else {
            setSelectedItems(item);
            if (typeof callback === 'function') {
                callback(item);
            }
            setIsComponentVisible(false);
            setSearchQuery("");
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const isFullWidth = isWidthInPercentage || width === "100" || width === "100%" || !width;
    const containerStyle = !isFullWidth && width
        ? { width: `${width}px` }
        : undefined;

    const hasSelection = multiSelect
        ? Boolean(selectedItems && selectedItems.length > 0)
        : Boolean(selectedItems);

    const displayTitle = multiSelect
        ? (selectedItems && selectedItems.length > 0
            ? selectedItems.map(item => getItemLabel(item)).join(", ")
            : noItemSelectedTitle)
        : (selectedItems
            ? getItemLabel(selectedItems)
            : noItemSelectedTitle);

    return (
        <div
            className={`flex flex-col relative ${isFullWidth ? 'w-full' : 'inline-block'} ${className}`}
            ref={ref}
            style={containerStyle}
        >
            <button
                id={`${id}open-select-item-dropdown`}
                type="button"
                disabled={disabled}
                onClick={handleOpenMenu}
                title={displayTitle}
                aria-haspopup="listbox"
                aria-expanded={isComponentVisible}
                className={`
                    relative w-full flex items-center justify-between gap-2 bg-white border rounded-lg
                    min-h-[40px] px-3.5 py-2 text-sm text-left
                    transition-all duration-150 focus:outline-none
                    ${error ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary'}
                    ${isComponentVisible ? 'border-primary ring-2 ring-primary/20 shadow-sm' : ''}
                    ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-75' : 'cursor-pointer hover:border-gray-400'}
                    ${selectClassName}
                `}
            >
                <div className="flex-1 flex flex-wrap items-center gap-1.5 overflow-hidden">
                    {hasSelection && !disabled ? (
                        <span className="text-gray-900 text-sm truncate">{displayTitle}</span>
                    ) : (
                        <span className="text-gray-400 text-sm select-none truncate">{noItemSelectedTitle}</span>
                    )}
                </div>

                <div className="flex items-center gap-1.5 shrink-0 text-gray-400">
                    <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                            isComponentVisible ? 'rotate-180 text-primary' : ''
                        }`}
                    />
                </div>
            </button>

            {/* Floating Popover Options Menu - identical to Select */}
            {isComponentVisible && (
                <div
                    role="listbox"
                    className={`stride-dropdown-menu left-0 right-0 ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} ${menuClassName}`}
                >
                    {searchable && (
                        <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
                            <Search size={14} className="text-gray-400 shrink-0 ml-1" />
                            <input
                                type="text"
                                className="w-full bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none py-1"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="text-gray-400 hover:text-gray-600 p-0.5 focus:outline-none"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )}

                    <div className={`max-h-60 overflow-y-auto py-1.5 ${joyRideState.run ? "overflow-hidden" : ""}`}>
                        {filteredItems && filteredItems.length > 0 ? (
                            filteredItems.map((item, index) => {
                                const itemKey = getItemKey(item);
                                const selectedKey = getItemKey(selectedItems);
                                const isSelected = itemKey !== null && (
                                    multiSelect
                                        ? selectedItems?.some((selected) => getItemKey(selected) === itemKey)
                                        : selectedKey !== null && selectedKey === itemKey
                                );

                                return (
                                    <div
                                        key={item?.val || item?.id || item?._id || index}
                                        id={index === 0 ? "select-first-item-from-dropdown" : ""}
                                        role="option"
                                        aria-selected={isSelected}
                                        onClick={() => handleSelect(item)}
                                        className={`
                                            px-3.5 py-2 text-sm flex items-center justify-between gap-3 cursor-pointer
                                            transition-colors duration-100
                                            ${isSelected ? 'bg-primary-soft text-primary font-medium' : 'text-stride-body hover:bg-surface'}
                                            ${item?.disabled ? 'opacity-40 cursor-not-allowed bg-gray-50' : ''}
                                        `}
                                    >
                                        <div className="flex items-center gap-2.5 truncate">
                                            {multiSelect && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    className="mr-1 rounded border-gray-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                                                    readOnly
                                                />
                                            )}
                                            {item?.icon && <span className="shrink-0">{item.icon}</span>}
                                            <div className="truncate">
                                                <div className="truncate">{getItemLabel(item)}</div>
                                                {item?.description && (
                                                    <div className="text-xs text-gray-400 font-normal truncate">
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {isSelected && !multiSelect && (
                                            <Check size={16} className="text-primary shrink-0" />
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-4 py-3 text-center text-xs text-gray-400 select-none">
                                No options found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dropdown;
